import { FC } from "react";
import { FormattedMessage } from "react-intl";

import { Box, Stack, TextField, Typography } from "@mui/material";

import type { IMultiAnalysisTemplateLayout } from "../../utils/analysis-template-settings";

const TEMPLATE_LAYOUT_MAX_ROWS = 4;
const TEMPLATE_LAYOUT_MAX_COLUMNS = 4;

interface ITemplateLayoutSelectorProps {
  layout: IMultiAnalysisTemplateLayout;
  disabled?: boolean;
  onChange: (layout: IMultiAnalysisTemplateLayout) => void;
}

const clampLayoutValue = (value: number, max: number): number => {
  if (!Number.isFinite(value)) {
    return 1;
  }

  return Math.max(1, Math.min(max, Math.trunc(value)));
};

export const TemplateLayoutSelector: FC<ITemplateLayoutSelectorProps> = ({ layout, disabled = false, onChange }) => {
  const handleRowsChange = (value: string) => {
    onChange({
      ...layout,
      rows: clampLayoutValue(Number(value), TEMPLATE_LAYOUT_MAX_ROWS),
    });
  };

  const handleColumnsChange = (value: string) => {
    onChange({
      ...layout,
      columns: clampLayoutValue(Number(value), TEMPLATE_LAYOUT_MAX_COLUMNS),
    });
  };

  const handleGridSelect = (rows: number, columns: number) => {
    if (disabled) {
      return;
    }

    onChange({ rows, columns });
  };

  return (
    <Stack spacing={1.25}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#4f5d75" }}>
        <FormattedMessage id="pages.multiAnalysis.templateEditor.tableDimensions" />
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          type="number"
          label={<FormattedMessage id="pages.multiAnalysis.templateEditor.columns" />}
          value={layout.columns}
          disabled={disabled}
          inputProps={{ min: 1, max: TEMPLATE_LAYOUT_MAX_COLUMNS }}
          onChange={event => handleColumnsChange(event.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          type="number"
          label={<FormattedMessage id="pages.multiAnalysis.templateEditor.rows" />}
          value={layout.rows}
          disabled={disabled}
          inputProps={{ min: 1, max: TEMPLATE_LAYOUT_MAX_ROWS }}
          onChange={event => handleRowsChange(event.target.value)}
          sx={{ flex: 1 }}
        />
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${TEMPLATE_LAYOUT_MAX_COLUMNS}, 20px)`,
          gridTemplateRows: `repeat(${TEMPLATE_LAYOUT_MAX_ROWS}, 20px)`,
          gap: "4px",
          width: "fit-content",
        }}
      >
        {Array.from({ length: TEMPLATE_LAYOUT_MAX_ROWS }).map((_, rowIndex) =>
          Array.from({ length: TEMPLATE_LAYOUT_MAX_COLUMNS }).map((__, columnIndex) => {
            const row = rowIndex + 1;
            const column = columnIndex + 1;
            const selected = row <= layout.rows && column <= layout.columns;

            return (
              <Box
                key={`${row}:${column}`}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={() => handleGridSelect(row, column)}
                onKeyDown={event => {
                  if ((event.key === "Enter" || event.key === " ") && !disabled) {
                    event.preventDefault();
                    handleGridSelect(row, column);
                  }
                }}
                sx={{
                  width: 20,
                  height: 20,
                  border: "1px solid #b8c0d4",
                  bgcolor: selected ? "#f28c28" : "#fff",
                  cursor: disabled ? "default" : "pointer",
                }}
              />
            );
          }),
        )}
      </Box>
    </Stack>
  );
};
